/* -------------------------------------------------------------------------- */
/*                        DistributionChannelsDropdown                         */
/* -------------------------------------------------------------------------- */
// WD-972 — Selector de Canal de Distribución con opción de crear inline.
// Valor = distributionChannelId (single) o number[] (multiple).
// Reutilizable: panes de Cliente/Sucursal (single) y, a futuro, el Formulario (multiple).

import { useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Label } from 'reactstrap';
import CreatableSelect from 'react-select/creatable';
import camelcaseKeys from 'camelcase-keys';

import * as distributionChannelActions from 'views/screens/DistributionChannels/actions';
import { getDistributionChannels } from 'views/screens/DistributionChannels/reducers';
import { saveDistributionChannelService } from 'views/screens/DistributionChannels/services';
import { DistributionChannel } from 'views/screens/DistributionChannels/types';

interface Membership {
  organizationId?: number | null;
  divisionId?: number | null;
  subdivisionId?: number | null;
}

interface Props {
  name: string;
  control: any;
  membership?: Membership;
  label?: string;
  required?: boolean;
  isMultiple?: boolean;
  colSize?: string;
  defaultValue?: any;
}

const DistributionChannelsDropdown = (props: Props) => {
  const {
    name,
    control,
    membership,
    label,
    required = false,
    isMultiple = false,
    colSize = '12',
    defaultValue
  } = props;

  const intl = useIntl();
  const dispatch = useDispatch();

  const channels: DistributionChannel[] = useSelector(getDistributionChannels);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    dispatch(distributionChannelActions.startFetchingDistributionChannels());
  }, [dispatch]);

  // Opciones filtradas por la organización de la membership (si viene)
  const options = useMemo(() => {
    const orgId = membership?.organizationId ?? null;
    return channels
      .filter((c) => (orgId ? c.organizationId === orgId : true))
      .map((c) => ({ value: c.distributionChannelId, label: c.name }));
  }, [channels, membership?.organizationId]);

  const findOption = (value: number) => options.find((o) => o.value === value) ?? null;

  const handleCreate = (inputValue: string, field: any) => {
    const trimmed = (inputValue ?? '').trim();
    if (!trimmed) return;
    setIsCreating(true);
    saveDistributionChannelService(
      {
        distributionChannelId: null,
        name: trimmed,
        organizationId: membership?.organizationId ?? null,
        divisionId: membership?.divisionId ?? null,
        subdivisionId: membership?.subdivisionId ?? null
      },
      {
        successAction: (response: any) => {
          const saved = camelcaseKeys(response.data.distributionChannel);
          // Inyectar en redux para que aparezca como opción (y en el tab de Clientes)
          dispatch(distributionChannelActions.completeSavingDistributionChannel(saved));
          if (isMultiple) {
            const current: number[] = Array.isArray(field.value) ? field.value : [];
            field.onChange([...current, saved.distributionChannelId]);
          } else {
            field.onChange(saved.distributionChannelId);
          }
          setIsCreating(false);
        },
        errorAction: () => setIsCreating(false)
      }
    );
  };

  return (
    <Col sm={colSize}>
      <div className="mb-1">
        {label && (
          <Label className="d-block">
            {label}
            {required && <span className="text-danger"> *</span>}
          </Label>
        )}
        <Controller
          control={control}
          name={name}
          defaultValue={defaultValue}
          rules={{ required }}
          render={({ field }) => {
            const value = isMultiple
              ? (Array.isArray(field.value) ? field.value : [])
                  .map((v: number) => findOption(v))
                  .filter(Boolean)
              : field.value != null
              ? findOption(field.value)
              : null;

            return (
              <CreatableSelect
                classNamePrefix="distributionChannelsDropdown"
                isMulti={isMultiple}
                isClearable
                isDisabled={isCreating}
                isLoading={isCreating}
                options={options}
                value={value}
                placeholder={intl.formatMessage({ id: 'distributionChannels.searchPlaceholder' })}
                noOptionsMessage={() =>
                  intl.formatMessage({ id: 'distributionChannels.noChannels' })
                }
                formatCreateLabel={(inputValue: string) => `＋ ${'"'}${inputValue}${'"'}`}
                onCreateOption={(inputValue: string) => handleCreate(inputValue, field)}
                onChange={(selected: any) => {
                  if (isMultiple) {
                    field.onChange((selected ?? []).map((o: any) => o.value));
                  } else {
                    field.onChange(selected ? selected.value : null);
                  }
                }}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                styles={{
                  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                  // Valor único como badge gris (igual que los chips de emails de formularios)
                  singleValue: (base: any) => ({
                    ...base,
                    backgroundColor: '#e6e6e6',
                    borderRadius: 3,
                    padding: '2px 8px',
                    color: '#333',
                    fontSize: 13,
                    width: 'fit-content',
                    maxWidth: 'calc(100% - 8px)'
                  })
                }}
              />
            );
          }}
        />
      </div>
    </Col>
  );
};

export default DistributionChannelsDropdown;
